/**
 * WCAG 2.1 relative luminance and contrast ratio utilities.
 * Used to warn when a bucket color fails AA contrast (4.5:1) against white text.
 */

function toLinear(c: number): number {
  const s = c / 255
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = luminance(hex1)
  const l2 = luminance(hex2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/** Returns true if the color passes AA contrast against white (#ffffff). */
export function passesAAOnWhite(hex: string): boolean {
  return contrastRatio(hex, '#ffffff') >= 4.5
}

/** Returns true if the color passes AA contrast against near-black (#1a1a1a). */
export function passesAAOnDark(hex: string): boolean {
  return contrastRatio(hex, '#1a1a1a') >= 4.5
}

/**
 * Given a bucket hex color, return the foreground color (white or near-black)
 * that achieves the higher contrast ratio.
 */
export function foregroundForColor(hex: string): '#ffffff' | '#1a1a1a' {
  const onWhite = contrastRatio(hex, '#ffffff')
  const onDark = contrastRatio(hex, '#1a1a1a')
  return onWhite >= onDark ? '#ffffff' : '#1a1a1a'
}

/** Default bucket color palette — all pass AA on white or dark text. */
export const BUCKET_PALETTE = [
  '#7C5CBF', // violet
  '#3B6EA5', // slate blue
  '#4A7C59', // forest green
  '#C4822A', // warm amber
  '#B54E4E', // muted rose
  '#2A8B8B', // teal
  '#8B5E3C', // warm brown
  '#6B7C8B', // cool grey-blue
  '#7A4A9E', // purple
  '#5E7C3A', // sage
  '#A06030', // terracotta
  '#3A7A6A', // seafoam
]
